/**
 * Shipments (§18 /shipments, §16) — read-only list of created shipments: order
 * ref, store, carrier/service, tracking (linked when present), status, label,
 * created. Filters (store · carrier · status), pagination, recent-first. Empty
 * until carriers create labels — designed empty state. Row opens a detail drawer.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { STORE_REGIONS, SHIPMENT_STATUSES } from "@cairn/shared";
import { api, type ShipmentRow } from "../lib/api.js";
import { humanizeServiceCode, humanizeCode, storeBadge, storeName } from "../lib/format.js";
import { formatAge } from "../lib/age.js";
import {
  Field,
  Select,
  SearchBar,
  Badge,
  Tag,
  Icon,
  Button,
  CarrierMark,
  EmptyState,
  ErrorState,
  SkeletonRows,
  DataTable,
  type Column,
} from "../components/ui/index.js";
import { ShipmentDrawer } from "./shipments/ShipmentDrawer.js";

const LIMIT = 50;

const COLUMNS: Array<Column<ShipmentRow>> = [
  {
    key: "order",
    header: "Order",
    cell: (s) => (
      <span className="cell-stack">
        <span className="cell-strong">{s.orderName}</span>
        <span className="cell-sub">{storeName(s)}</span>
      </span>
    ),
  },
  { key: "store", header: "Store", cell: (s) => <Badge title={storeName(s)}>{storeBadge(s)}</Badge> },
  {
    key: "carrier",
    header: "Carrier",
    cell: (s) => (
      <span className="q-carrier">
        <CarrierMark code={s.carrierCode} showName />
        <span className="cell-sub" title={s.serviceCode}>
          {humanizeServiceCode(s.serviceCode)}
        </span>
      </span>
    ),
  },
  {
    key: "tracking",
    header: "Tracking",
    cell: (s) =>
      s.trackingNumber ? (
        s.trackingUrl ? (
          <a
            href={s.trackingUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="cell-mono"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            {s.trackingNumber} <Icon name="external" size={12} />
          </a>
        ) : (
          <span className="cell-mono">{s.trackingNumber}</span>
        )
      ) : (
        <span className="muted">—</span>
      ),
  },
  { key: "status", header: "Status", cell: (s) => <Badge>{humanizeCode(s.status)}</Badge> },
  { key: "label", header: "Label", cell: (s) => (s.labelAvailable ? <Tag>label</Tag> : <span className="muted">—</span>) },
  { key: "created", header: "Created", cell: (s) => <span title={s.createdAt}>{formatAge(s.createdAt)}</span> },
];

export function Shipments() {
  const [storeId, setStoreId] = useState("");
  const [carrierCode, setCarrierCode] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState(""); // Task 1 — order # / tracking / carrier
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<ShipmentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (storeId) qs.set("storeId", storeId);
      if (carrierCode) qs.set("carrierCode", carrierCode);
      if (status) qs.set("status", status);
      if (search.trim()) qs.set("q", search.trim());
      qs.set("page", String(page));
      qs.set("limit", String(LIMIT));
      const res = await api.getShipments(qs);
      setRows(res.shipments);
      setTotal(res.total);
      setHasNext(res.hasNext);
    } catch {
      setRows([]);
      setTotal(0);
      setHasNext(false);
      setError("Couldn’t load shipments. Retry in a moment.");
    } finally {
      setLoading(false);
    }
  }, [storeId, carrierCode, status, search, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyPredicate = useCallback((fn: () => void) => {
    setPage(1);
    fn();
  }, []);

  const carriers = useMemo(
    () => Array.from(new Set(rows.map((r) => r.carrierCode))).sort(),
    [rows],
  );

  return (
    <section className="shipments">
      <div className="page-head">
        <div className="page-head__title">
          <h1>Shipments</h1>
          <span className="page-head__count">
            {total} shipment{total === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="toolbar">
        <SearchBar
          className="toolbar__search"
          ariaLabel="Search shipments"
          placeholder="Search order #, tracking #, carrier…"
          focusShortcut="/"
          value={search}
          onChange={(v) => applyPredicate(() => setSearch(v))}
        />
        <Field label="Store">
          <Select
            ariaLabel="Filter by store"
            value={storeId}
            onChange={(v) => applyPredicate(() => setStoreId(v))}
            options={[{ value: "", label: "All stores" }, ...STORE_REGIONS.map((r) => ({ value: r, label: r }))]}
          />
        </Field>
        <Field label="Carrier">
          <Select
            ariaLabel="Filter by carrier"
            value={carrierCode}
            onChange={(v) => applyPredicate(() => setCarrierCode(v))}
            options={[{ value: "", label: "All carriers" }, ...carriers.map((c) => ({ value: c, label: c }))]}
          />
        </Field>
        <Field label="Status">
          <Select
            ariaLabel="Filter by status"
            value={status}
            onChange={(v) => applyPredicate(() => setStatus(v))}
            options={[{ value: "", label: "All" }, ...SHIPMENT_STATUSES.map((s) => ({ value: s, label: humanizeCode(s) }))]}
          />
        </Field>
      </div>

      {error ? (
        <ErrorState title="Couldn’t load shipments" hint="The worker may be busy. Retry in a moment." onRetry={() => void load()} />
      ) : loading && rows.length === 0 ? (
        <div className="table-card">
          <SkeletonRows rows={8} cols={7} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon="truck"
          title="No shipments yet"
          hint="Shipments appear here once orders are packed and carriers create labels (a later increment)."
        />
      ) : (
        <DataTable
          ariaLabel="Shipments"
          rows={rows}
          getRowKey={(s) => s.id}
          selectedKey={selectedId}
          onRowOpen={(s) => setSelectedId(s.id)}
          columns={COLUMNS}
        />
      )}

      {(rows.length > 0 || page > 1) && (
        <footer className="pager">
          <Button variant="secondary" size="sm" icon="chevronLeft" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Prev
          </Button>
          <span className="pager__info">
            Page {page}
            {total > 0 ? ` · ${Math.min((page - 1) * LIMIT + 1, total)}–${Math.min(page * LIMIT, total)} of ${total}` : ""}
          </span>
          <Button variant="secondary" size="sm" disabled={!hasNext || loading} onClick={() => setPage((p) => p + 1)}>
            Next
            <Icon name="chevronRight" />
          </Button>
        </footer>
      )}

      {selectedId && <ShipmentDrawer id={selectedId} onClose={() => setSelectedId(null)} />}
    </section>
  );
}
